import type { Order } from '@/api/orders/orders-types'
import type { NestedSkid, SkidLabel, SkidListItem } from '@/api/skids/skids-types'

function skidOrdersMatchOrder(skid: SkidListItem, order: Order): boolean {
  return skid.orders?.some((o) => o === order.autoid) ?? false
}

function skidOrdersMatchAutoids(skid: SkidListItem, autoids: Set<string>): boolean {
  return skid.orders?.some((o) => autoids.has(o)) ?? false
}

function emptySkidPlaceholder(id: number): SkidListItem {
  return {
    id,
    name: null,
    created_at: null,
    skid_type: 'SKI',
    is_complete: null,
    box_id: null,
    orders: []
  }
}

/**
 * Skids tied to this order: bundle `skid_id` references plus skids whose
 * `orders[]` contains the order's autoid (canonical ownership key) or a
 * detail-level autoid (legacy/defensive — kept so historical rows still
 * surface). Sorted by id. Unknown ids only referenced by bundles use a
 * minimal placeholder until list data loads.
 */
export function getOrderSkids(order: Order, skids: SkidListItem[]): SkidListItem[] {
  const byId = new Map(skids.map((s) => [s.id, s] as const))
  const detailAutoids = new Set((order.details ?? []).map((d) => d.autoid).filter(Boolean) as string[])
  const result: SkidListItem[] = []
  const seen = new Set<number>()

  const push = (s: SkidListItem) => {
    if (seen.has(s.id)) return
    seen.add(s.id)
    result.push(s)
  }

  for (const d of order.details) {
    for (const b of d.bundles ?? []) {
      if (b.skid_id == null) continue
      push(byId.get(b.skid_id) ?? emptySkidPlaceholder(b.skid_id))
    }
  }
  for (const s of skids) {
    if (skidOrdersMatchOrder(s, order) || skidOrdersMatchAutoids(s, detailAutoids)) {
      push(s)
    }
  }

  return result.sort((a, b) => a.id - b.id)
}

/**
 * Promote a NestedSkid (as embedded on `Order.skids[]`) to a SkidListItem shape for consumers
 * that only read `id`/`name`/`skid_type`/`is_complete`/`orders`. `created_at`/`bundles`/`lhbs`
 * aren't returned by the nested shape, so we fill them with safe defaults.
 */
export function nestedSkidToListItem(skid: NestedSkid): SkidListItem {
  return {
    id: skid.id,
    name: skid.name ?? null,
    created_at: null,
    skid_type: skid.skid_type,
    is_complete: skid.is_complete ?? null,
    box_id: skid.box_id ?? null,
    mos_id: skid.mos_id ?? null,
    width: skid.width ?? null,
    length: skid.length ?? null,
    orders: skid.orders ?? [],
    label: skid.label ?? null
  }
}

/**
 * Collect every unique `order.skids[]` entry across the given orders. Intended for packaging
 * flows where the user only ever resolves SKI/LHB belonging to loaded orders — lets us avoid
 * a separate `GET /skids/` request.
 */
export function collectSkidsFromOrders(orders: Order[]): SkidListItem[] {
  const byId = new Map<number, SkidListItem>()
  for (const order of orders) {
    for (const skid of order.skids ?? []) {
      if (!byId.has(skid.id)) byId.set(skid.id, nestedSkidToListItem(skid))
    }
  }
  return Array.from(byId.values())
}

/**
 * Total weight of the bundles sitting on this skid, read from the order's details.
 * `null` when no bundle on the skid carries a weight (EBMS leaves it empty).
 */
export function getOrderSkidWeight(order: Order, skidId: number): number | null {
  let total = 0
  let hasWeight = false
  for (const detail of order.details ?? []) {
    for (const bundle of detail.bundles ?? []) {
      if (bundle.skid_id !== skidId || bundle.weight == null) continue
      hasWeight = true
      total += bundle.weight
    }
  }
  return hasWeight ? total : null
}

/**
 * The 4x4 pallet Maco uses for soffit skids. The backend has no flag for it, only numeric
 * `width`/`length`, so `48 x 48` is the marker: a normal skid seeds at length 120 and only
 * ever grows, so 48 can never turn up on one by accident.
 */
export const PALLET_4X4 = { width: 48, length: 48 } as const

/**
 * What the operator can build: three pallet widths in inches, plus the fixed 4x4 pallet
 * used for soffit skids. `length` is only sent for the 4x4 — the others take the backend
 * default of 120 and grow as longer parts are scanned on.
 */
export const SKID_SIZES = [
  { key: '24', label: '24', width: 24 },
  { key: '36', label: '36', width: 36 },
  { key: '48', label: '48', width: 48 },
  { key: '4x4', label: '4x4', width: PALLET_4X4.width, length: PALLET_4X4.length }
] as const

export type SkidSizeOption = (typeof SKID_SIZES)[number]

/** The picker option a skid currently matches, `null` before a width was chosen. */
export function skidSizeOption(skid: Pick<SkidListItem, 'width' | 'length'>): SkidSizeOption | null {
  if (skid.width == null) return null
  if (isPallet4x4(skid)) return SKID_SIZES[3]
  return SKID_SIZES.find((s) => !('length' in s) && s.width === skid.width) ?? null
}

/**
 * PATCH body for resizing a skid that already has parts on it. A plain width keeps the
 * grown length; leaving a 4x4 has to grow it back from 48 to cover what is already packed,
 * since scans only bump length when a part lands.
 */
export function resizeSkidPayload(
  skid: Pick<SkidListItem, 'width' | 'length'>,
  next: SkidSizeOption,
  partLengths: Array<number | null | undefined>
): { width: number; length?: number } {
  if ('length' in next) return { width: next.width, length: next.length }
  if (!isPallet4x4(skid)) return { width: next.width }
  const longest = Math.max(
    120,
    ...partLengths.map((l) => (l != null && Number.isFinite(l) ? Math.ceil(l) : 0))
  )
  return { width: next.width, length: longest }
}

/** What `formatSkidSize` returns for a 4x4 — a name, not a measurement. */
export const PALLET_4X4_LABEL = '4x4 pallet'

function isPallet4x4(skid: Pick<SkidListItem, 'width' | 'length'>): boolean {
  return skid.width === PALLET_4X4.width && skid.length === PALLET_4X4.length
}

/**
 * `formatSkidSize` output with its unit, for the places that print `48 x 120 in`.
 * The 4x4 label is left alone — `4x4 pallet in` reads as a typo.
 */
export function formatSkidSizeWithUnit(size: string | null | undefined): string | null {
  if (!size) return null
  return size === PALLET_4X4_LABEL ? size : `${size} in`
}

/**
 * `48 x 120` in inches — width comes from the operator's 24/36/48 pick, length starts at
 * the backend default of 120 and grows as longer parts are scanned on. `null` until a
 * width is chosen, since `? x 120` tells nobody anything. The 4x4 pallet prints by name:
 * its size is fixed, so a number pair says nothing an operator needs.
 */
export function formatSkidSize(skid: Pick<SkidListItem, 'width' | 'length'>): string | null {
  if (skid.width == null) return null
  if (isPallet4x4(skid)) return PALLET_4X4_LABEL
  return `${skid.width} x ${skid.length ?? 120}`
}

/**
 * Length the skid should carry once `detail` sits on it: the longest part wins. The pallet
 * itself stays 10 ft — this is the overall packaged length, which is what shipping needs.
 * Returns `null` when nothing has to change, including for a 4x4 pallet.
 */
export function resolveSkidLengthBump(
  currentLength: number | null | undefined,
  detailLength: number | null | undefined
): number | null {
  if (detailLength == null || !Number.isFinite(detailLength) || detailLength <= 0) return null
  // 4x4 pallet: fixed size, never grows, whatever gets scanned onto it.
  if (currentLength === PALLET_4X4.length) return null
  const current = currentLength ?? 120
  return detailLength > current ? Math.ceil(detailLength) : null
}

/** Label shown on packaging skid pills (matches `name` from API when set). */
export function formatPackagingSkidBadgeLabel(skid: SkidListItem): string {
  const name = skid.name?.trim()
  if (name) return name

  if (skid.skid_type === 'LHB') {
    const o = skid.orders?.[0]
    if (o?.includes('-')) {
      const tail = o.slice(o.indexOf('-') + 1)
      if (tail) return `LHB-${tail}`
    }
    return `LHB-${skid.id}`
  }

  if (skid.skid_type === 'OUT') return `OUT-${skid.id}`

  return `SKD-${skid.id}`
}

/** The shop's code for each LHB `label`; an LHB with no label is a plain package. */
const PACKAGE_CODES = { Bundle: 'BDL', Box: 'BX', Coil: 'CL' } as const satisfies Record<SkidLabel, string>

export type PackageCode = (typeof PACKAGE_CODES)[SkidLabel] | 'PKG'

/**
 * A box holding exactly one bundle — what the backend makes for each bundle of a boxed
 * product (M120, M48). That bundle's sticker already prints the BX id, so the box needs no
 * unit sticker of its own. Hand-made boxes with several bundles (or none yet) are ordinary units.
 */
export const isSingleBundleBox = (skid: { label?: SkidLabel | null; bundles?: unknown[] }): boolean =>
  skid.label === 'Box' && skid.bundles?.length === 1

/** `BDL` / `BX` / `CL` for a labelled LHB, `PKG` otherwise — including labels this build doesn't know. */
export function packageCode(label: SkidLabel | null | undefined): PackageCode {
  return (label && PACKAGE_CODES[label]) || 'PKG'
}

/**
 * What people read for a shipping unit: an LHB's `LHB-` prefix becomes the shop's code
 * (`BDL-0923-6316`, `PKG-…`). Display only — the backend name stays the identity: skid
 * sticker QRs encode it and the skid panel looks units up by it, so never pass this to
 * `setSkidPanelKey`, a search, or a sticker's QR value.
 */
export function formatShippingUnitName(skid: SkidListItem): string {
  const name = formatPackagingSkidBadgeLabel(skid)
  if (skid.skid_type !== 'LHB') return name
  return name.replace(/^LHB(?=-|$)/i, packageCode(skid.label))
}

/**
 * Undoes `formatShippingUnitName` for typed or scanned text: `BDL-0923-6316` (what the
 * screen and sticker text show) back to the backend's `LHB-0923-6316`, so a name read off
 * the page still resolves. Anything else passes through.
 */
export function toBackendUnitName(input: string): string {
  return input.replace(/^(BDL|BX|CL|PKG)(?=-|$)/i, 'LHB')
}
